import { ValidSteps } from '../enums';
import { ICheckpointUnitChangeDto } from '../interfaces';

/**
 * A trip is a sequence of sections ("tramos"). Section 1 is the trip header unit; every
 * `unitChange` anchored on a stopover checkpoint opens the next one. Sections are always
 * derived from the checkpoints, never stored — see DESENGANCHE_UNIT_CHANGE_PLAN.md.
 */
export interface ITripSectionUnit {
    carrierId?: string;
    vehicleId?: string;
    driverId?: string;
    semitrailerIds?: string[];
}

export interface ITripSection extends ITripSectionUnit {
    number: number;
    startCheckpointIndex: number;
    /** No tracking unit assigned yet — a valid, expected state (Escenario 2). */
    isPending: boolean;
}

interface HasUnitChange {
    unitChange?: ICheckpointUnitChangeDto;
}

interface HasSteps {
    steps?: { type?: string; triggeredAt?: string }[];
}

/** The unit actually in charge of the trip right now, denormalized onto the trip so Mongo can sort and filter by it. */
export interface ITripCurrentUnit extends ITripSectionUnit {
    sectionNumber: number;
}

/**
 * A unit change with no vehicle, driver or semitrailer is "pendiente de asignación": the operator
 * knows the unit changes here but not yet to what.
 *
 */
export const isPendingUnitChange = (unitChange?: ICheckpointUnitChangeDto): boolean =>
    !!unitChange && !unitChange.vehicleId && !unitChange.driverId && !unitChange.semitrailerIds?.length;

/**
 * Applies a unit change over the incoming unit.
 *
 * The vehicle and driver are **always explicit**: the form pre-fills them from the previous section,
 * so an empty value means "unassigned", not "same as before". Inheriting them would silently undo a
 * deliberate clear and make the change look like no change at all.
 *
 * The carrier and semitrailer do inherit when absent — the carrier is not a tracking resource, and
 * the semi follows the whole trip rather than the section.
 */
export const applyUnitChange = (
    incoming: ITripSectionUnit,
    unitChange: ICheckpointUnitChangeDto,
): ITripSectionUnit => ({
    carrierId: unitChange.carrierId ?? incoming.carrierId,
    vehicleId: unitChange.vehicleId,
    driverId: unitChange.driverId,
    semitrailerIds: unitChange.semitrailerIds?.length ? unitChange.semitrailerIds : incoming.semitrailerIds,
});

export const buildTripSections = <T extends HasUnitChange>(
    header: ITripSectionUnit,
    checkpoints: T[],
): ITripSection[] => {
    const sections: ITripSection[] = [{ ...header, number: 1, startCheckpointIndex: 0, isPending: false }];

    checkpoints?.forEach((checkpoint, index) => {
        if (!checkpoint?.unitChange) return;

        const incoming = sections[sections.length - 1];
        sections.push({
            ...applyUnitChange(incoming, checkpoint.unitChange),
            number: sections.length + 1,
            startCheckpointIndex: index,
            isPending: isPendingUnitChange(checkpoint.unitChange),
        });
    });

    return sections;
};

/** Unit arriving AT the given checkpoint (i.e. before its own unit change is applied). */
export const getIncomingUnitForCheckpoint = <T extends HasUnitChange>(
    header: ITripSectionUnit,
    checkpoints: T[],
    checkpointIndex: number,
): ITripSectionUnit => {
    const sections = buildTripSections(header, checkpoints);
    const section = [...sections].reverse().find((s) => s.startCheckpointIndex < checkpointIndex);

    return section ?? sections[0];
};

/** Unit leaving FROM the given checkpoint (i.e. after its own unit change is applied). */
export const getEffectiveUnitForCheckpoint = <T extends HasUnitChange>(
    header: ITripSectionUnit,
    checkpoints: T[],
    checkpointIndex: number,
): ITripSectionUnit => {
    const sections = buildTripSections(header, checkpoints);
    const section = [...sections].reverse().find((s) => s.startCheckpointIndex <= checkpointIndex);

    return section ?? sections[0];
};

/**
 * Section still serving the stop when the vehicle arrives — the stop is worked by the previous
 * unit, and the new one only takes over on departure. This is what the UI labels a stop with.
 */
export const getIncomingSectionNumberForCheckpoint = <T extends HasUnitChange>(
    header: ITripSectionUnit,
    checkpoints: T[],
    checkpointIndex: number,
): number => {
    const sections = buildTripSections(header, checkpoints);

    return [...sections].reverse().find((s) => s.startCheckpointIndex < checkpointIndex)?.number ?? 1;
};

/**
 * Section serving the stop on departure — i.e. the one its own unit change opens. This is what
 * the Registro reports, since the log describes the section the change creates.
 */
export const getEffectiveSectionNumberForCheckpoint = <T extends HasUnitChange>(
    header: ITripSectionUnit,
    checkpoints: T[],
    checkpointIndex: number,
): number => {
    const sections = buildTripSections(header, checkpoints);

    return [...sections].reverse().find((s) => s.startCheckpointIndex <= checkpointIndex)?.number ?? 1;
};

/**
 * Every step of the stop has been triggered — the vehicle arrived *and* left. On a desenganche anchor
 * that is the moment the handover takes effect, and therefore the moment the outgoing unit is free.
 */
export const isCheckpointThrough = <T extends HasSteps>(checkpoint?: T): boolean =>
    !!checkpoint?.steps?.length && checkpoint.steps.every((step) => !!step?.triggeredAt);

const ENTRY_STEP_TYPES: string[] = [ValidSteps.IN_AREA, ValidSteps.CUSTOMER_ARRIVAL];

/**
 * The stop has been reached: its arrival step is triggered. On a desenganche anchor this is the
 * boundary of the handover — the outgoing unit is at the yard from here on, and waiting for its
 * departure instead would stall the trip whenever that truck stays parked.
 */
export const hasEntryStepTriggered = <T extends HasSteps>(checkpoint?: T): boolean =>
    !!checkpoint?.steps?.some((step) => ENTRY_STEP_TYPES.includes(step?.type ?? '') && !!step?.triggeredAt);

/**
 * Index of the last handover the trip has reached, or -1.
 *
 * This is the single definition of "the unit changed for real": the table view, the `originalUnit`
 * freeze rule and the write that moves the trip root all key off this same index, so they cannot
 * disagree. A stop whose steps carry no arrival type falls back to being fully triggered.
 *
 * Reaching the anchor is what this answers — whether the incoming unit is actually there is a
 * live check the trigger path makes before moving the root, not something derivable from the trip.
 */
export const getLastCompletedUnitChangeIndex = <T extends { unitChange?: unknown } & HasSteps>(
    checkpoints: T[],
): number =>
    (checkpoints ?? []).reduce(
        (last, checkpoint, index) =>
            checkpoint?.unitChange && (hasEntryStepTriggered(checkpoint) || isCheckpointThrough(checkpoint))
                ? index
                : last,
        -1,
    );

/**
 * The unit in charge of the trip right now: the header until a handover completes, the section it
 * opens from then on. A trip with no unit change always yields the header and `sectionNumber: 1`.
 *
 * This is the value the trip **root** is meant to take once the handover write lands — pass it the
 * original unit (`trip.originalUnit`) as the header, never the root itself, or a root that has
 * already moved would be folded a second time. Nothing calls it yet.
 */
export const getCurrentUnitForTrip = <T extends HasUnitChange & HasSteps>(
    header: ITripSectionUnit,
    checkpoints: T[],
): ITripCurrentUnit => {
    const index = getLastCompletedUnitChangeIndex(checkpoints);
    // Picked field by field on purpose: `getEffectiveUnitForCheckpoint` hands back the whole
    // section, and spreading it would leak `number` / `isPending` / `startCheckpointIndex` into
    // what gets persisted.
    const unit = index < 0 ? header : getEffectiveUnitForCheckpoint(header, checkpoints, index);

    return {
        carrierId: unit.carrierId,
        vehicleId: unit.vehicleId,
        driverId: unit.driverId,
        semitrailerIds: unit.semitrailerIds,
        sectionNumber: index < 0 ? 1 : getEffectiveSectionNumberForCheckpoint(header, checkpoints, index),
    };
};

const sameSemitrailers = (a?: string[], b?: string[]): boolean => {
    const left = (a ?? []).filter(Boolean);
    const right = (b ?? []).filter(Boolean);

    return left.length === right.length && left.every((id) => right.includes(id));
};

/**
 * A unit change is only real when vehicle, driver or semitrailers differ from the incoming
 * section. Changing the carrier alone is not a unit change.
 */
export const isRealUnitChange = (incoming: ITripSectionUnit, unitChange?: ICheckpointUnitChangeDto): boolean => {
    if (!unitChange) return false;
    // A pending change is a placeholder, not a change — callers must allow it separately.
    if (isPendingUnitChange(unitChange)) return false;

    const effective = applyUnitChange(incoming, unitChange);

    return (
        effective.vehicleId !== incoming.vehicleId ||
        effective.driverId !== incoming.driverId ||
        !sameSemitrailers(effective.semitrailerIds, incoming.semitrailerIds)
    );
};

/** A resource still committed to an open trip, and why it is not free yet. */
export interface ITripOccupiedResources {
    vehicleIds: string[];
    driverIds: string[];
    semitrailerIds: string[];
}

/**
 * What an open trip still holds. Vehicle and driver are freed when their section ends (drop & hook);
 * the semi is held for the whole trip. A section not started yet still counts as occupied.
 */
export const getOccupiedResourcesForTrip = <T extends HasUnitChange & HasSteps>(
    header: ITripSectionUnit,
    checkpoints: T[],
): ITripOccupiedResources => {
    const sections = buildTripSections(header, checkpoints);
    const vehicleIds = new Set<string>();
    const driverIds = new Set<string>();
    const semitrailerIds = new Set<string>();

    sections.forEach((section, index) => {
        // The section ends where the next one begins; the last one ends with the trip
        const nextAnchorIndex = sections[index + 1]?.startCheckpointIndex;
        const isOver = nextAnchorIndex !== undefined && isCheckpointThrough(checkpoints?.[nextAnchorIndex]);

        if (!isOver) {
            if (section.vehicleId) vehicleIds.add(section.vehicleId);
            if (section.driverId) driverIds.add(section.driverId);
        }
        (section.semitrailerIds ?? []).forEach((id) => id && semitrailerIds.add(id));
    });

    return {
        vehicleIds: [...vehicleIds],
        driverIds: [...driverIds],
        semitrailerIds: [...semitrailerIds],
    };
};

/**
 * A section plus where it ends. `endCheckpointIndex` is inclusive and is the next section's own
 * start — the desenganche stop belongs to both, which is what makes legs and stops partition.
 */
export interface ITripSectionRange extends ITripSection {
    endCheckpointIndex: number;
}

export const buildTripSectionRanges = <T extends HasUnitChange>(
    header: ITripSectionUnit,
    checkpoints: T[],
): ITripSectionRange[] => {
    const sections = buildTripSections(header, checkpoints);
    const lastIndex = Math.max((checkpoints?.length ?? 0) - 1, 0);

    return sections.map((section, index) => ({
        ...section,
        endCheckpointIndex: sections[index + 1]?.startCheckpointIndex ?? lastIndex,
    }));
};

interface HasLegMeters {
    traveledMetersAfter?: number;
    theoreticalMetersAfter?: number;
}

export interface ITripSectionMeters {
    traveled: number;
    theoretical: number;
    /** `traveled` when it is greater than 0, otherwise the theoretical estimate. */
    meters: number;
    isEstimated: boolean;
}

/**
 * Distance of a section. The legs inside it are the ones leaving each of its stops but the last,
 * so the ranges of a trip cover every leg exactly once and their sum is the trip's own distance.
 */
export const getSectionMeters = <T extends HasLegMeters>(
    checkpoints: T[],
    startIndex: number,
    endIndex: number,
): ITripSectionMeters => {
    const legs = (checkpoints ?? []).slice(startIndex, Math.max(endIndex, startIndex));
    const traveled = legs.reduce((total, checkpoint) => total + (checkpoint?.traveledMetersAfter ?? 0), 0);
    const theoretical = legs.reduce((total, checkpoint) => total + (checkpoint?.theoreticalMetersAfter ?? 0), 0);
    const meters = traveled > 0 ? traveled : theoretical;

    return { traveled, theoretical, meters, isEstimated: traveled <= 0 };
};
